/**
 * "Turn your phone sideways" — the portrait blocker for touch devices.
 *
 * The touch layout (joystick left, ability cluster right, HUD across the top)
 * is designed for landscape only. In portrait the cluster covers half the
 * arena and the camera sees a sliver, so rather than ship a second layout the
 * game asks for the one it has.
 *
 * `screen.orientation.lock('landscape')` only works in fullscreen (Chrome on
 * Android) and not at all on iPhone, so calling it on boot, as this used to,
 * silently did nothing. The button here asks for fullscreen FIRST, from a user
 * gesture, which is the one path where the lock actually takes. Where
 * fullscreen does not exist (iPhone Safari) the button is not offered and the
 * prompt just asks the player to rotate.
 *
 * Visibility is pure CSS (`@media (orientation: portrait)`), so a rotation shows
 * or hides it with no listener, and inside the shell's iframe the media query
 * reads the frame's own box, which is the one that matters.
 */

let _installed = false;

function canFullscreen() {
  const el = document.documentElement;
  return !!(document.fullscreenEnabled && el.requestFullscreen) ||
    !!(document.webkitFullscreenEnabled && el.webkitRequestFullscreen);
}

async function goLandscape() {
  const el = document.documentElement;
  try {
    if (!document.fullscreenElement && !document.webkitFullscreenElement) {
      await (el.requestFullscreen?.({ navigationUI: 'hide' }) ?? el.webkitRequestFullscreen?.());
    }
  } catch {
    // Refused (no gesture, iframe without allow="fullscreen"): the prompt stays
    // up and the player rotates by hand.
  }
  try {
    await screen.orientation?.lock?.('landscape');
  } catch {
    // Not supported or not fullscreen. Same fallback.
  }
}

export function setupRotatePrompt() {
  if (_installed) return;
  _installed = true;

  const el = document.createElement('div');
  el.id = 'rotate-prompt';
  el.setAttribute('role', 'alert');
  el.innerHTML = `
    <div class="rotate-card">
      <div class="rotate-phone" aria-hidden="true"><span></span></div>
      <div class="rotate-title">Rotate your phone</div>
      <div class="rotate-sub">RWA Royale plays in landscape</div>
      ${canFullscreen() ? '<button type="button" class="rotate-btn">Play fullscreen</button>' : ''}
    </div>
  `;
  el.querySelector('.rotate-btn')?.addEventListener('click', () => void goLandscape());
  // The blocker eats every touch under it: a thumb resting on the glass must
  // not steer or cast through it.
  for (const type of ['touchstart', 'touchmove', 'pointerdown']) {
    el.addEventListener(type, (e) => e.stopPropagation(), { passive: true });
  }
  document.body.append(el);
}
